/* eslint-disable react/jsx-indent, react/jsx-closing-tag-location */
import React, { useCallback, useEffect, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  lighten,
  Paper,
  Popper,
  Typography,
  alpha,
  useTheme
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FilterAlt as FilterAltIcon,
  Groups as GroupsIcon,
  ReceiptLong as ReceiptLongIcon
} from '@mui/icons-material'

import { FilterPopper } from './filterPopper'
import { DataTranspositionChargesPanel } from './dataTranspositionChargesPanel'
import { DASHBOARD_BAR_Z, DASHBOARD_POPPER_Z } from './dashboardLayerZ'
import { N8N_TAB_ORDER, N8N_TAB_SHORT_LABELS } from '../../utils/socrCatalogAdapter'

const BAR_PANEL_TRANSITION = '0.3s ease-in-out'

// Bottom bar: clients popper, issue filters, charge categories; expands upward when open.
const DataTranspositionBar = ({ props: viewProps = {}, controls = {} }) => {
  const {
    dataTranspositionOpen,
    clientSelected,
    clientPopperOpen,
    clientAnchorEl,
    dataSource,
    filterPopper,
    vulnUpgradesLoading,
    filtersSelected,
    billableSelected,
    billingChargePopperOpen,
    selectedChargeCategoryCount,
    allChargeOptions,
    chargesPresentInData,
    selectedCharges,
    chargeCategorySubtitle,
    sourceEnabledOptions,
    sourceDisabledOptions,
    filterPopperRef,
    billingChargeAnchorRef,
    graphFilters,
    qboBillingRows,
    excludedChargeRowKeys,
    clientSettings,
    n8nTabCapabilities = {}
  } = viewProps

  const {
    setDataTranspositionOpen,
    toggleClientPopper,
    closeClientPopper,
    handleClearAll,
    handleClientChange,
    setFilterPopper,
    handleFilterChange,
    handleBillableChange,
    setBillingChargePopperOpen,
    handleClearIssueFilters,
    handleClearCharges,
    handleChargeCategoryToggle,
    handleChargeRowInGraphToggle,
    handleSelectAllVisibleCharges
  } = controls

  // Keep the charges panel mounted once opened so Collapse can animate without remounting Chart.js.
  const [chargesPanelMounted, setChargesPanelMounted] = useState(false)
  const toggleDataTranspositionOpen = useCallback(() => {
    setDataTranspositionOpen((v) => {
      const next = !v
      if (next) setChargesPanelMounted(true)
      return next
    })
  }, [setDataTranspositionOpen])

  useEffect(() => {
    if (dataTranspositionOpen) setChargesPanelMounted(true)
  }, [dataTranspositionOpen])

  const theme = useTheme()
  const bill = theme.custom.colors.billingDrawer
  const bar = theme.custom.colors.dataTranspositionBar

  const clientFromGoogle = dataSource === 'google'
  const clientAccent = clientFromGoogle ? theme.palette.success.main : theme.custom.keysys.blue

  const clientCount = Object.values(clientSelected || {}).filter(Boolean).length
  const issueFilterCount = Object.values(filtersSelected || {}).filter(Boolean).length

  const badgeAnchorSx = {
    fontWeight: 800,
    right: 12,
    top: 10,
    zIndex: 2,
    pointerEvents: 'none'
  }

  const badgeWrapSx = {
    width: '100%',
    display: 'flex',
    '& .MuiBadge-badge': badgeAnchorSx
  }

  const tileShellSx = {
    borderRadius: 1.5,
    border: bar.tileBorder,
    backgroundColor: bar.tileBackground,
    boxShadow: bar.tileInsetShadow,
    overflow: 'visible',
    minHeight: 52,
    position: 'relative'
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: DASHBOARD_BAR_Z,
        pointerEvents: 'none'
      }}
    >
      <Box
        sx={{
          pointerEvents: 'auto',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch'
        }}
      >
        <Box
          role='button'
          tabIndex={0}
          onClick={toggleDataTranspositionOpen}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              toggleDataTranspositionOpen()
            }
          }}
          aria-label={dataTranspositionOpen ? 'Close data transposition drawer' : 'Open data transposition drawer'}
          sx={{
            px: { xs: 1, sm: 1.5, md: 2 },
            pt: { xs: 0.75, sm: 1 },
            pb: 0,
            borderRadius: dataTranspositionOpen ? '12px 12px 0 0' : '18px 18px 0 0',
            border: bar.panelBorder,
            borderBottom: dataTranspositionOpen ? 'none' : bar.panelBorder,
            backgroundColor: bar.panelSurface,
            backgroundImage: bar.panelGradient,
            backdropFilter: 'blur(10px)',
            boxShadow: dataTranspositionOpen ? bar.panelShadowUpOpen : bar.panelShadowUp,
            cursor: 'pointer',
            transition: [
              `border-radius ${BAR_PANEL_TRANSITION}`,
              `box-shadow ${BAR_PANEL_TRANSITION}`
            ].join(', ')
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              pb: 0.75,
              pt: 0.25,
              pointerEvents: 'none',
              color: theme.palette.common.white,
              gap: 0.75
            }}
          >
            {(dataTranspositionOpen ? <ExpandMoreIcon sx={{ fontSize: 26, opacity: 0.95 }} /> : <ExpandLessIcon sx={{ fontSize: 26, opacity: 0.95 }} />)}
            <Typography
              variant='overline'
              sx={{
                fontWeight: 900,
                letterSpacing: 1.1,
                opacity: 0.95,
                lineHeight: 1.2,
                userSelect: 'none'
              }}
            >
              Data Transposition
            </Typography>
            {(dataTranspositionOpen ? <ExpandMoreIcon sx={{ fontSize: 26, opacity: 0.95 }} /> : <ExpandLessIcon sx={{ fontSize: 26, opacity: 0.95 }} />)}
          </Box>

          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              display: 'flex',
              alignItems: 'stretch',
              justifyContent: 'space-between',
              gap: { xs: 1, sm: 1.25 },
              width: '100%',
              maxWidth: '100%',
              mx: 0,
              pb: 1
            }}
          >
            <Box sx={{ flex: '1 1 0', width: 0, minWidth: 0, position: 'relative' }}>
              <Box sx={tileShellSx}>
                <Badge badgeContent={clientCount} color='primary' showZero sx={badgeWrapSx}>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleClientPopper(e)
                    }}
                    fullWidth
                    variant='outlined'
                    startIcon={<GroupsIcon />}
                    endIcon={clientPopperOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{
                      borderRadius: 1.5,
                      border: 'none',
                      py: 1.35,
                      minHeight: 52,
                      fontWeight: 900,
                      textTransform: 'none',
                      justifyContent: 'space-between',
                      borderColor: dataSource === 'google'
                        ? alpha(theme.palette.success.light, 0.8)
                        : alpha(theme.custom.keysys.blue, 0.9),
                      color: dataSource === 'google' ? theme.palette.success.light : lighten(theme.custom.keysys.blue, 0.25),
                      backgroundColor: dataSource === 'google'
                        ? alpha(theme.palette.success.main, 0.28)
                        : alpha(theme.custom.keysys.blue, 0.28),
                      '&:hover': {
                        backgroundColor: dataSource === 'google'
                          ? alpha(theme.palette.success.main, 0.4)
                          : alpha(theme.custom.keysys.blue, 0.4)
                      }
                    }}
                  >
                    Clients
                  </Button>
                </Badge>
              </Box>

              <Popper open={clientPopperOpen} anchorEl={clientAnchorEl} placement='top-start' sx={{ zIndex: DASHBOARD_POPPER_Z }}>
                <ClickAwayListener onClickAway={closeClientPopper}>
                  <Paper
                    elevation={10}
                    sx={{
                      mb: 1,
                      width: { xs: 'min(calc(100vw - 32px), 520px)', sm: 520 },
                      maxHeight: 420,
                      overflow: 'auto',
                      borderRadius: 2,
                      border: `1px solid ${bill.border}`,
                      backgroundColor: bill.surface
                    }}
                  >
                    <Box
                      sx={{
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 1
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant='subtitle2' sx={{ fontWeight: 800, color: bill.text }}>
                          Clients graphed
                        </Typography>
                        <Typography variant='caption' sx={{ color: bill.muted, display: 'block' }}>
                          {dataSource === 'n8n'
                            ? 'Badges show which AI tabs each client can open'
                            : 'Select which clients appear in the chart'}
                        </Typography>
                      </Box>
                      <Button
                        onClick={() => {
                          handleClearAll()
                          closeClientPopper()
                        }}
                        size='small'
                        variant='text'
                        sx={{ color: theme.palette.error.light, flexShrink: 0 }}
                      >
                        Clear
                      </Button>
                    </Box>
                    <Divider sx={{ borderColor: bill.border }} />
                    <Box sx={{ p: 1.5 }}>
                      <FormControl component='fieldset' variant='standard' sx={{ width: '100%' }}>
                        <FormGroup>
                          {[...sourceEnabledOptions]
                            .sort((a, b) => {
                              const aSelected = clientSelected?.[a.name] || false
                              const bSelected = clientSelected?.[b.name] || false
                              if (aSelected && !bSelected) return -1
                              if (!aSelected && bSelected) return 1
                              return String(a.name || '').localeCompare(String(b.name || ''))
                            })
                            .map(({ name: clientName }) => {
                              if (typeof clientName !== 'string' || !clientName) return null
                              const caps = n8nTabCapabilities?.[clientName] || {}
                              const availableTabs = dataSource === 'n8n'
                                ? N8N_TAB_ORDER.filter((tab) => caps[tab])
                                : []
                              return (
                                <FormControlLabel
                                  key={`client-${clientName}`}
                                  control={(
                                    <Checkbox
                                      name={clientName}
                                      checked={clientSelected?.[clientName] || false}
                                      onChange={handleClientChange}
                                      size='small'
                                      sx={{ '&.Mui-checked': { color: clientAccent } }}
                                    />
                                  )}
                                  label={(
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        columnGap: 1,
                                        rowGap: 0.4,
                                        py: 0.15,
                                        width: '100%',
                                        minWidth: 0
                                      }}
                                    >
                                      <Typography
                                        variant='body2'
                                        sx={{
                                          fontWeight: 600,
                                          color: bill.text,
                                          lineHeight: 1.35,
                                          flex: '1 1 auto',
                                          minWidth: '40%'
                                        }}
                                      >
                                        {clientName}
                                      </Typography>
                                      {availableTabs.length > 0
                                        ? (
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              flexWrap: 'wrap',
                                              justifyContent: 'flex-end',
                                              gap: 0.4,
                                              flex: '1 1 auto',
                                              minWidth: 'fit-content',
                                              ml: 'auto'
                                            }}
                                          >
                                            {availableTabs.map((tab) => (
                                              <Box
                                                key={`${clientName}-${tab}`}
                                                component='span'
                                                sx={{
                                                  fontSize: '0.65rem',
                                                  fontWeight: 800,
                                                  letterSpacing: 0.2,
                                                  lineHeight: 1.2,
                                                  px: 0.55,
                                                  py: 0.2,
                                                  borderRadius: 0.75,
                                                  color: alpha(bill.text, 0.88),
                                                  backgroundColor: alpha(clientAccent, 0.16),
                                                  border: `1px solid ${alpha(clientAccent, 0.28)}`
                                                }}
                                              >
                                                {N8N_TAB_SHORT_LABELS[tab] || tab}
                                              </Box>
                                            ))}
                                          </Box>
                                          )
                                        : null}
                                    </Box>
                                  )}
                                  sx={{
                                    m: 0,
                                    alignItems: 'flex-start',
                                    py: 0.45,
                                    width: '100%',
                                    mr: 0,
                                    '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 }
                                  }}
                                />
                              )
                            })}
                        </FormGroup>
                      </FormControl>
                    </Box>

                    {sourceDisabledOptions.length > 0 && (
                      <>
                        <Divider sx={{ borderColor: alpha(bill.border, 0.85) }} />
                        <Box sx={{ p: 1.5 }}>
                          <Typography variant='caption' sx={{ color: alpha(bill.muted, 0.95), fontWeight: 800, letterSpacing: 0.4, display: 'block', mb: 0.75 }}>
                            {dataSource === 'google' ? 'N8N only (disabled on Google)' : 'Google only (disabled on N8N)'}
                          </Typography>
                          <FormControl component='fieldset' variant='standard' sx={{ width: '100%' }}>
                            <FormGroup>
                              {[...sourceDisabledOptions]
                                .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
                                .map(({ name: clientName }) => (
                                  <FormControlLabel
                                    key={`disabled-client-${clientName}`}
                                    control={<Checkbox checked={false} disabled size='small' />}
                                    label={(
                                      <Typography variant='body2' sx={{ fontWeight: 600, color: alpha(bill.text, 0.35), lineHeight: 1.35 }}>
                                        {clientName}
                                      </Typography>
                                    )}
                                    sx={{ m: 0, alignItems: 'center', py: 0.35 }}
                                  />
                                ))}
                            </FormGroup>
                          </FormControl>
                        </Box>
                      </>
                    )}
                  </Paper>
                </ClickAwayListener>
              </Popper>
            </Box>

            <Box sx={{ flex: '1 1 0', width: 0, minWidth: 0, position: 'relative' }}>
              <Box sx={tileShellSx}>
                <Badge
                  badgeContent={issueFilterCount}
                  showZero
                  invisible={dataSource !== 'n8n'}
                  sx={{
                    ...badgeWrapSx,
                    '& .MuiBadge-badge': {
                      ...badgeAnchorSx,
                      backgroundColor: theme.palette.success.main,
                      color: theme.palette.common.white
                    }
                  }}
                >
                  <Button
                    onClick={(e) => {
                      e.stopPropagation()
                      setFilterPopper(!filterPopper)
                    }}
                    ref={filterPopperRef}
                    fullWidth
                    variant='outlined'
                    startIcon={<FilterAltIcon />}
                    endIcon={!filterPopper ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                    disabled={dataSource !== 'n8n'}
                    sx={{
                      borderRadius: 1.5,
                      border: 'none',
                      py: 1.35,
                      minHeight: 52,
                      fontWeight: 900,
                      textTransform: 'none',
                      justifyContent: 'space-between',
                      borderColor: alpha(theme.palette.common.white, 0.22),
                      color: theme.palette.text.primary,
                      backgroundColor: alpha(theme.palette.success.main, 0.26),
                      '&:hover': { backgroundColor: alpha(theme.palette.success.main, 0.36) },
                      '&:disabled': {
                        color: alpha(theme.palette.text.primary, 0.38),
                        backgroundColor: alpha(theme.palette.success.main, 0.08)
                      }
                    }}
                  >
                    Issue types
                  </Button>
                </Badge>
              </Box>

              <FilterPopper
                theme={theme}
                open={filterPopper}
                anchorRef={filterPopperRef}
                disabled={dataSource !== 'n8n'}
                vulnUpgradesLoading={vulnUpgradesLoading}
                filtersSelected={filtersSelected}
                onToggleOpen={() => setFilterPopper(!filterPopper)}
                onFilterChange={handleFilterChange}
                graphFilters={graphFilters}
                billableSelected={billableSelected}
                onBillableChange={handleBillableChange}
                onClear={handleClearIssueFilters}
              />
            </Box>

            <Box sx={{ flex: '1 1 0', width: 0, minWidth: 0, position: 'relative' }}>
              <Box sx={tileShellSx}>
                <Badge
                  badgeContent={selectedChargeCategoryCount}
                  showZero
                  sx={{
                    ...badgeWrapSx,
                    '& .MuiBadge-badge': {
                      ...badgeWrapSx['& .MuiBadge-badge'],
                      backgroundColor: bill.accent,
                      color: theme.palette.common.white
                    }
                  }}
                >
                  <Button
                    ref={billingChargeAnchorRef}
                    fullWidth
                    variant='outlined'
                    onClick={(e) => {
                      e.stopPropagation()
                      setBillingChargePopperOpen((o) => !o)
                    }}
                    startIcon={<ReceiptLongIcon sx={{ color: bill.accent }} />}
                    endIcon={billingChargePopperOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{
                      borderRadius: 1.5,
                      border: 'none',
                      py: 1.35,
                      minHeight: 52,
                      fontWeight: 900,
                      textTransform: 'none',
                      justifyContent: 'space-between',
                      borderColor: alpha(bill.accent, 0.55),
                      color: theme.palette.text.primary,
                      backgroundColor: alpha(bill.accent, 0.22),
                      '&:hover': { backgroundColor: alpha(bill.accent, 0.32) }
                    }}
                  >
                    Charges
                  </Button>
                </Badge>
              </Box>

              <Popper open={billingChargePopperOpen} anchorEl={billingChargeAnchorRef.current} placement='top-end' sx={{ zIndex: DASHBOARD_POPPER_Z }}>
                <ClickAwayListener
                  onClickAway={(e) => {
                    if (billingChargeAnchorRef.current?.contains(e.target)) return
                    setBillingChargePopperOpen(false)
                  }}
                >
                  <Paper
                    elevation={10}
                    sx={{
                      mb: 1,
                      width: { xs: 'min(calc(100vw - 32px), 520px)', sm: 520 },
                      maxHeight: 420,
                      overflow: 'auto',
                      borderRadius: 2,
                      border: `1px solid ${bill.border}`,
                      backgroundColor: bill.surface
                    }}
                  >
                    <Box
                      sx={{
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 1
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant='subtitle2' sx={{ fontWeight: 800, color: bill.text }}>
                          Charges
                        </Typography>
                        <Typography variant='caption' sx={{ color: bill.muted, display: 'block' }}>
                          Enabled charges are first. Disabled charges are listed below.
                        </Typography>
                      </Box>
                      <Button
                        onClick={() => {
                          handleClearCharges()
                        }}
                        size='small'
                        variant='text'
                        sx={{ color: theme.palette.error.light, flexShrink: 0 }}
                      >
                        Clear
                      </Button>
                    </Box>
                    <Divider sx={{ borderColor: bill.border }} />
                    <Box sx={{ p: 1.5 }}>
                      <FormControl component='fieldset' variant='standard' sx={{ width: '100%' }}>
                        <FormGroup>
                          {allChargeOptions
                            .filter((c) => chargesPresentInData.has(c))
                            .map((chargeName) => {
                              const checked = Boolean(selectedCharges?.[chargeName])
                              return (
                                <FormControlLabel
                                  key={`charge-enabled-${chargeName}`}
                                  control={(
                                    <Checkbox
                                      name={chargeName}
                                      checked={checked}
                                      onChange={(e) => {
                                        handleChargeCategoryToggle(chargeName, e.target.checked)
                                      }}
                                      size='small'
                                      sx={{ '&.Mui-checked': { color: bill.accent } }}
                                    />
                                  )}
                                  label={(
                                    <Box>
                                      <Typography variant='body2' sx={{ fontWeight: 600, color: bill.text, lineHeight: 1.35 }}>
                                        {chargeName}
                                      </Typography>
                                      <Typography variant='caption' sx={{ color: bill.muted, display: 'block' }}>
                                        {chargeCategorySubtitle(chargeName)}
                                      </Typography>
                                    </Box>
                                  )}
                                  sx={{ m: 0, alignItems: 'flex-start', py: 0.5 }}
                                />
                              )
                            })}
                        </FormGroup>
                      </FormControl>
                    </Box>

                    {allChargeOptions.some((c) => !chargesPresentInData.has(c)) && (
                      <>
                        <Divider sx={{ borderColor: alpha(bill.border, 0.85) }} />
                        <Box sx={{ p: 1.5 }}>
                          <Typography variant='caption' sx={{ color: alpha(bill.muted, 0.9), fontWeight: 800, letterSpacing: 0.4 }}>
                            Not available (disabled)
                          </Typography>
                          <FormControl component='fieldset' variant='standard' sx={{ width: '100%', mt: 0.75 }}>
                            <FormGroup>
                              {allChargeOptions
                                .filter((c) => !chargesPresentInData.has(c))
                                .map((chargeName) => (
                                  <FormControlLabel
                                    key={`charge-disabled-${chargeName}`}
                                    control={<Checkbox checked={false} disabled size='small' />}
                                    label={(
                                      <Box>
                                        <Typography variant='body2' sx={{ fontWeight: 600, color: alpha(bill.text, 0.35), lineHeight: 1.35 }}>
                                          {chargeName}
                                        </Typography>
                                        <Typography variant='caption' sx={{ color: alpha(bill.muted, 0.8), display: 'block' }}>
                                          Not available for selected clients / date range
                                        </Typography>
                                      </Box>
                                    )}
                                    sx={{ m: 0, alignItems: 'flex-start', py: 0.5 }}
                                  />
                                ))}
                            </FormGroup>
                          </FormControl>
                        </Box>
                      </>
                    )}
                  </Paper>
                </ClickAwayListener>
              </Popper>
            </Box>
          </Box>
        </Box>

        {chargesPanelMounted && (
          <Collapse
            in={dataTranspositionOpen}
            timeout={280}
            easing={{ enter: 'cubic-bezier(0.4, 0, 0.2, 1)', exit: 'cubic-bezier(0.4, 0, 1, 1)' }}
            unmountOnExit={false}
            sx={{ width: '100%' }}
          >
            <Box
              sx={{
                borderLeft: bar.panelBorder,
                borderRight: bar.panelBorder,
                borderBottom: bar.panelBorder,
                borderBottomLeftRadius: 12,
                borderBottomRightRadius: 12,
                backgroundColor: bar.panelSurface,
                backgroundImage: bar.panelGradient,
                boxShadow: bar.panelShadowUpOpen,
                px: { xs: 7, sm: 7.5, md: 8 },
                pb: 1.5,
                pt: 1,
                minHeight: { xs: 'min(58vh, 520px)', sm: 'min(52vh, 560px)' },
                maxHeight: { xs: '72vh', sm: '68vh' },
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
            >
              <DataTranspositionChargesPanel
                qboBillingRows={qboBillingRows}
                allChargeOptions={allChargeOptions}
                chargesPresentInData={chargesPresentInData}
                selectedCharges={selectedCharges}
                excludedChargeRowKeys={excludedChargeRowKeys}
                clientSettings={clientSettings}
                onToggleRowInGraph={handleChargeRowInGraphToggle}
                onSelectAllVisible={handleSelectAllVisibleCharges}
              />
            </Box>
          </Collapse>
        )}
      </Box>
    </Box>
  )
}

export { DataTranspositionBar }
